'use client';

import { useState } from 'react';
import Image from 'next/image';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { GeneratedAvatar } from '@/components/ui/generated-avatar';
import { skipImageOptimizer } from '@/lib/image-optimizer';

/**
 * A person's avatar, in one of two forms, and every avatar of a person in the
 * app goes through one of them (generated-avatar.test.ts holds that), so each
 * surface follows the same rules:
 *
 *   - their picture, when they have one and it loads;
 *   - their initial on their colour (GeneratedAvatar) when they have none or
 *     it fails to load, so a missing picture never shows as a grey blank;
 *   - a quiet empty disc while a picture is still loading, never the initial,
 *     which would flash a colour before every real photo;
 *   - the plain silhouette only for someone with no uid to draw from.
 *
 * WHETHER an avatar shows at all is still each caller's decision, from the
 * account's showProfilePicture switch read the way that surface always has.
 * Nothing here turns an avatar on, and the initial never stands in for a
 * picture someone switched off.
 *
 * TWO FORMS, FOR TWO KINDS OF SPOT. UserAvatar is Radix's avatar, which renders
 * no <img> until the browser has loaded the picture after hydration. That suits
 * lists that fill in after load anyway (product cards, search, reviews,
 * messages, the Customers page) and keeps their pictures off the image
 * optimizer, as before. AvatarPicture is next/image, for the spots whose
 * picture is in the server's HTML (the storefront's nav and header, the
 * product page's byline, the owner's own profile), where waiting for
 * hydration would open the page on a blank and then swap.
 */

/** Whose avatar, and their picture if they have one. */
type Person = {
  /** The picture's URL. Empty, null or absent: they have none, and their initial is drawn. */
  src?: string | null;
  /**
   * Their uid, which picks the colour. Leave it out for someone with no
   * identity to draw (a deleted account, a guest, a picture switched off on a
   * page that always draws a slot): the plain silhouette shows instead.
   */
  seed?: string | null;
  /** Their username, whose first letter is drawn. */
  name?: string | null;
};

/** The picture's URL, or null for none (a blank string included). */
function pictureUrl(src: string | null | undefined): string | null {
  return typeof src === 'string' && src.trim() !== '' ? src : null;
}

/** Radix's loading status for the picture. */
export type AvatarImageStatus = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * A whole avatar (the round box, the picture and what stands in for it) for a
 * list. `className` sizes the box, as on Radix's Avatar.
 */
export function UserAvatar({
  src,
  seed,
  name,
  alt = '',
  className,
  onLoadingStatusChange,
}: Person & {
  /** The picture's alt text: '' (the default) where the name is beside it already. */
  alt?: string;
  className?: string;
  /** For a caller with its own loading indicator: it hears 'error' as well as 'loaded'. */
  onLoadingStatusChange?: (status: AvatarImageStatus) => void;
}) {
  const picture = pictureUrl(src);
  // The picture that failed, rather than a flag, so a new one is tried afresh.
  const [failed, setFailed] = useState<string | null>(null);
  const tryPicture = picture !== null && failed !== picture;
  return (
    <Avatar className={className}>
      {tryPicture && (
        <AvatarImage
          src={picture}
          alt={alt}
          onLoadingStatusChange={(status) => {
            if (status === 'error') setFailed(picture);
            onLoadingStatusChange?.(status);
          }}
        />
      )}
      {/* Radix shows the fallback until the picture has loaded: an empty disc
          while it might still, the initial once there is none to load. */}
      <AvatarFallback>{tryPicture ? null : <GeneratedAvatar seed={seed} name={name} />}</AvatarFallback>
    </Avatar>
  );
}

/**
 * The inside of an avatar whose picture is in the server's HTML: the picture
 * through next/image, filling its box, or the initial in its place. Put it in
 * a positioned, sized, round box that clips (Radix's Avatar root is one).
 *
 * A picture that fails turns into the initial, even when it failed before the
 * page hydrated: next/image re-runs the load once mounted whenever onError is
 * set, so the error that the server's <img> hit is heard here.
 */
export function AvatarPicture({
  src,
  seed,
  name,
  alt = '',
  sizes,
  unoptimized,
  onLoad,
  onError,
}: Person & {
  /** The picture's alt text: '' (the default) where the name is beside it already. */
  alt?: string;
  /** next/image's `sizes`: the box's width. */
  sizes: string;
  /** Always the original URL. By default only a foreign host skips the optimizer (skipImageOptimizer). */
  unoptimized?: boolean;
  onLoad?: () => void;
  /** Heard when the picture fails, after the initial has taken its place. */
  onError?: () => void;
}) {
  const picture = pictureUrl(src);
  const [failed, setFailed] = useState<string | null>(null);
  if (picture === null || failed === picture) return <GeneratedAvatar seed={seed} name={name} />;
  return (
    <Image
      src={picture}
      alt={alt}
      fill
      sizes={sizes}
      unoptimized={unoptimized ?? skipImageOptimizer(picture)}
      className="object-cover"
      onLoad={onLoad}
      onError={() => {
        setFailed(picture);
        onError?.();
      }}
    />
  );
}
